import PlatformTypeBadge from '@boComponents/PlatformTypeBadge';
import { faEraser, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import MultiSelectFilter from '@sharedComponents/MultiSelectFilter';
import Pagination from '@sharedComponents/Pagination';
import Panel from '@sharedComponents/Panel';
import SearchBar from '@sharedComponents/SearchBar';
import SegmentedControl from '@sharedComponents/SegmentedControl';
import Select from '@sharedComponents/Select';
import Sidebar from '@sharedComponents/Sidebar';
import Table from '@sharedComponents/Table';
import Typography from '@sharedComponents/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CLIENT_CATEGORIES, PLATFORM_TYPES } from '../helpers';
import { getPlatformColumns } from '../shared/PlatformRow';
import ClientFormPanel from './ClientFormPanel';
import { getClientColumns } from './ClientRow';

// Also the page size the caller (router container) is expected to request.
export const PAGE_SIZE = 20;
const TAB_CLIENT = 'client';
const TAB_PLATFORM = 'platform';

// Initial list query, also the router container's initial fetch query.
export const INITIAL_QUERY = { tab: TAB_CLIENT, search: '', status: 'ACTIVE', category: 'ALL', types: [], page: 1 };

const ClientList = ({
    items = [],
    total = 0,
    loading = false,
    users = [],
    currentUserUuid = null,
    onQueryChange = () => {},
    onCreate = () => {},
    onView = () => {},
    onViewPlatform = () => {},
    initialCreateOpen = false,
    initialTab = TAB_CLIENT,
} = {}) => {
    const { t } = useTranslation('bo');
    const searchBarRef = useRef();

    const [query, setQuery] = useState({ ...INITIAL_QUERY, tab: initialTab });
    // Latest query, read by updateQuery: the debounced SearchBar calls back with a handler captured earlier.
    const queryRef = useRef(query);
    const [createOpen, setCreateOpen] = useState(initialCreateOpen);

    const { tab: activeTab, search, status: statusFilter, category: categoryFilter, types: platformTypeFilter, page } = query;
    const isPlatformMode = activeTab === TAB_PLATFORM;

    // Applies a query change (any filter change resets the page) and notifies the container from the event
    // itself rather than from an effect: both re-render in the same batch, so the table never pairs one
    // tab's columns with the other tab's items. The container owns fetching and pagination.
    const updateQuery = (patch) => {
        const next = { ...queryRef.current, page: 1, ...patch };
        queryRef.current = next;
        setQuery(next);
        onQueryChange(next);
    };

    const tabOptions = [
        { value: TAB_CLIENT, label: t('By client') },
        { value: TAB_PLATFORM, label: t('By platform') },
    ];
    const categoryOptions = [
        // Feminine "all": the plain `All` key already reads "Tous" for the status filter.
        { value: 'ALL', label: t('All', { context: 'female' }) },
        ...CLIENT_CATEGORIES.map((category) => ({ value: category, label: category })),
        { value: 'NONE', label: t('No category') },
    ];
    const statusOptions = [
        { value: 'ACTIVE', label: t('Active') },
        { value: 'INACTIVE', label: t('Inactive') },
        { value: 'ALL', label: t('All') },
        { value: 'MULTI_PLATFORM', label: t('Multi-platform') },
    ];
    const platformTypeOptions = PLATFORM_TYPES.map((type) => ({ value: type, label: <PlatformTypeBadge type={type} /> }));

    const columns = isPlatformMode ? getPlatformColumns(t, { onViewPlatform }) : getClientColumns(t, { onView });

    const hasActiveFilters =
        search.trim().length > 0 ||
        (isPlatformMode ? platformTypeFilter.length > 0 : statusFilter !== 'ACTIVE' || categoryFilter !== 'ALL');
    const noResults = !loading && total === 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const handleTabChange = (tab) => updateQuery({ tab });
    const handleSearchChange = (text) => updateQuery({ search: text });
    const handleStatusChange = (opt) => updateQuery({ status: opt.value });
    const handleCategoryChange = (opt) => updateQuery({ category: opt.value });
    const handlePlatformTypeChange = (types) => updateQuery({ types });

    const handleReset = () => {
        // SearchBar is uncontrolled: clearing the query does not empty the visible input.
        searchBarRef.current?.setInputValue('');
        updateQuery({ search: '', status: INITIAL_QUERY.status, category: INITIAL_QUERY.category, types: INITIAL_QUERY.types });
    };

    const handleCreate = async (payload) => {
        await onCreate(payload);
        setCreateOpen(false);
    };

    return (
        <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                    {t('Clients')}
                </Typography>
                <Button icon={faPlus} label={t('New client')} action={() => setCreateOpen(true)} />
            </FlexBox>

            <Panel>
                <FlexBox justifyContent="space-between" alignItems="center" gap="s" additionnalStyles={{ padding: 'var(--space-m)' }}>
                    <SegmentedControl options={tabOptions} value={activeTab} onChange={handleTabChange} />
                    <FlexBox alignItems="center" gap="s">
                        {isPlatformMode ? (
                            <MultiSelectFilter
                                label={t('Platform type')}
                                options={platformTypeOptions}
                                value={platformTypeFilter}
                                onChange={handlePlatformTypeChange}
                            />
                        ) : (
                            <>
                                <FlexBox flexDirection="column" additionnalStyles={{ width: '220px' }}>
                                    {/* Without a category filter the short "Categories" placeholder shows instead of a
                                        long "all categories" value, which overflowed the fixed-width field. */}
                                    <Select
                                        options={categoryOptions}
                                        value={categoryFilter === 'ALL' ? null : categoryFilter}
                                        placeholder={t('Categories')}
                                        onChange={handleCategoryChange}
                                        isSearchable={false}
                                    />
                                </FlexBox>
                                <FlexBox flexDirection="column" additionnalStyles={{ width: '180px' }}>
                                    <Select
                                        options={statusOptions}
                                        value={statusFilter}
                                        onChange={handleStatusChange}
                                        isSearchable={false}
                                    />
                                </FlexBox>
                            </>
                        )}
                        <FlexBox additionnalStyles={{ width: '320px' }}>
                            <SearchBar
                                ref={searchBarRef}
                                hideMenu
                                compact
                                minChars={0}
                                debounceMs={300}
                                controlShouldRenderValue={false}
                                search={async () => []}
                                onTextChange={handleSearchChange}
                                onClear={() => handleSearchChange('')}
                                placeholder={isPlatformMode ? t('Search a platform') : t('Search a client')}
                            />
                        </FlexBox>
                    </FlexBox>
                </FlexBox>

                {noResults ? (
                    <FlexBox flexDirection="column" gap="m" alignItems="center" additionnalStyles={{ padding: 'var(--space-xl)' }}>
                        <Typography color="neutral-secondary">
                            {hasActiveFilters ? t('No client matches your search.') : t('No client yet.')}
                        </Typography>
                        {hasActiveFilters && <Button ghost icon={faEraser} label={t('Reset filters')} action={handleReset} />}
                    </FlexBox>
                ) : (
                    <Table columns={columns} items={items} loading={loading} borderless getRowKey={(item) => item.uuid} />
                )}

                {!noResults && (
                    <FlexBox
                        justifyContent="space-between"
                        alignItems="center"
                        additionnalStyles={{
                            borderTop: '1px solid var(--border-color-neutral-tertiary)',
                            padding: 'var(--space-xs) var(--space-m) var(--space-s)',
                        }}
                    >
                        <Typography fontSize="xs" color="neutral-secondary">
                            {total} {t('results')}
                        </Typography>
                        <Pagination totalPages={totalPages} selectedPage={page} onChange={(selected) => updateQuery({ page: selected })} />
                    </FlexBox>
                )}
            </Panel>

            <Sidebar isOpened={createOpen} onClose={() => setCreateOpen(false)} title={t('New client')}>
                <ClientFormPanel users={users} currentUserUuid={currentUserUuid} onCreate={handleCreate} />
            </Sidebar>
        </FlexBox>
    );
};

export default ClientList;
